'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { PrismFpsSample } from '@/components/ui/Prism';

// Health gate for the animated Prism hero background. Decides, per page view, whether the
// WebGL background is healthy enough to keep running or should be replaced by the static
// fallback. Designed so a HEALTHY prism that merely hiccups is never downgraded:
//
//   - It judges the per-second AVERAGE frame delta, never the worst frame. A lone GC/jank
//     spike barely moves a 1s average, so a single hiccup can't trip it; a real malfunction
//     has every frame slow, so the average is unambiguously bad.
//   - It requires the bad average to be SUSTAINED across several consecutive eligible
//     windows. Any single healthy window resets the streak.
//   - It only counts ELIGIBLE windows — after the entrance ramp, past cold-start warmup,
//     and while the tab is visible (rAF throttles when backgrounded; that's not a fault).
//   - Hard, unambiguous faults (WebGL creation/context loss, a browser-reported major
//     performance caveat, prefers-reduced-motion) downgrade immediately.
//
// Once downgraded it stays static for the page view (no flapping); a fresh load re-evaluates.

export enum PrismBackgroundMode {
  WebGL = 'webgl',
  Static = 'static',
}

enum PrismFpsWindowHealth {
  Healthy = 'healthy',
  Bad = 'bad',
  Severe = 'severe',
}

// Per-second average frame delta above this = the window is "bad" (~under 33fps sustained).
const PRISM_BAD_FRAME_DELTA_MS = 30;
// ...and above this = "severe" (~under 16fps), which downgrades on a shorter streak.
const PRISM_SEVERE_FRAME_DELTA_MS = 60;
const PRISM_BAD_STREAK_WINDOWS = 3;
const PRISM_SEVERE_STREAK_WINDOWS = 2;
// Ignore the first windows after mount (JIT warmup, asset decode, first compositor frames).
const PRISM_COLD_START_SKIP_WINDOWS = 2;
const PRISM_REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const PRISM_WEBGL_CONTEXT_ID = 'webgl';
const PRISM_EXPERIMENTAL_WEBGL_CONTEXT_ID = 'experimental-webgl';
const PRISM_WEBGL_LOSE_CONTEXT_EXTENSION = 'WEBGL_lose_context';
const PRISM_DOCUMENT_VISIBLE_STATE = 'visible';

interface PrismHealthGateInput {
  // True once the entrance animation has effectively finished, so its deliberate
  // scale/glow ramp isn't mistaken for bad frames. Owned by the caller (Cherobackground).
  isEntranceComplete: () => boolean;
}

export interface PrismHealthGate {
  mode: PrismBackgroundMode;
  recordFpsSample: (sample: PrismFpsSample) => void;
  reportPrismFailure: () => void;
}

interface PrismFpsStreaks {
  badStreak: number;
  severeStreak: number;
}

function isPrismDocumentVisible(): boolean {
  return (
    typeof document === 'undefined' ||
    document.visibilityState === PRISM_DOCUMENT_VISIBLE_STATE
  );
}

function isPrismFpsSampleEligible(input: {
  entranceComplete: boolean;
  windowsSeen: number;
}): boolean {
  return (
    isPrismDocumentVisible() &&
    input.entranceComplete &&
    input.windowsSeen > PRISM_COLD_START_SKIP_WINDOWS
  );
}

function getPrismFpsWindowHealth({
  avgFrameDeltaMs,
}: PrismFpsSample): PrismFpsWindowHealth {
  if (avgFrameDeltaMs > PRISM_SEVERE_FRAME_DELTA_MS) {
    return PrismFpsWindowHealth.Severe;
  }

  if (avgFrameDeltaMs > PRISM_BAD_FRAME_DELTA_MS) {
    return PrismFpsWindowHealth.Bad;
  }

  return PrismFpsWindowHealth.Healthy;
}

function getNextPrismFpsStreaks(input: {
  health: PrismFpsWindowHealth;
  streaks: PrismFpsStreaks;
}): PrismFpsStreaks {
  if (input.health === PrismFpsWindowHealth.Severe) {
    return {
      badStreak: input.streaks.badStreak + 1,
      severeStreak: input.streaks.severeStreak + 1,
    };
  }

  if (input.health === PrismFpsWindowHealth.Bad) {
    return {
      badStreak: input.streaks.badStreak + 1,
      severeStreak: 0,
    };
  }

  return {
    badStreak: 0,
    severeStreak: 0,
  };
}

function shouldDowngradeForFpsStreaks({
  badStreak,
  severeStreak,
}: PrismFpsStreaks): boolean {
  return (
    severeStreak >= PRISM_SEVERE_STREAK_WINDOWS ||
    badStreak >= PRISM_BAD_STREAK_WINDOWS
  );
}

export function usePrismHealthGate({ isEntranceComplete }: PrismHealthGateInput): PrismHealthGate {
  const [mode, setMode] = useState<PrismBackgroundMode>(PrismBackgroundMode.WebGL);

  const settledRef = useRef(false);
  const badStreakRef = useRef(0);
  const severeStreakRef = useRef(0);
  const windowsSeenRef = useRef(0);

  const downgrade = useCallback((): void => {
    if (settledRef.current) return;
    settledRef.current = true;
    setMode(PrismBackgroundMode.Static);
  }, []);

  // One-time hard checks at mount (client-only, SSR-safe — runs in an effect).
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (window.matchMedia?.(PRISM_REDUCED_MOTION_QUERY).matches) {
      downgrade();
      return;
    }

    // Ask the browser itself whether a performant (non-software) context is available.
    // A null result is the browser declaring a "major performance caveat" — trust it.
    let caveat = false;
    try {
      const probeCanvas = document.createElement('canvas');
      const probeGl =
        (probeCanvas.getContext(PRISM_WEBGL_CONTEXT_ID, {
          failIfMajorPerformanceCaveat: true,
        }) as WebGLRenderingContext | null) ??
        (probeCanvas.getContext(PRISM_EXPERIMENTAL_WEBGL_CONTEXT_ID, {
          failIfMajorPerformanceCaveat: true,
        }) as WebGLRenderingContext | null);
      if (!probeGl) {
        caveat = true;
      } else {
        probeGl.getExtension(PRISM_WEBGL_LOSE_CONTEXT_EXTENSION)?.loseContext();
      }
    } catch {
      caveat = true;
    }
    if (caveat) downgrade();
  }, [downgrade]);

  const recordFpsSample = useCallback(
    (sample: PrismFpsSample): void => {
      if (settledRef.current) return;
      windowsSeenRef.current += 1;

      if (
        !isPrismFpsSampleEligible({
          entranceComplete: isEntranceComplete(),
          windowsSeen: windowsSeenRef.current,
        })
      ) {
        return;
      }

      const nextStreaks = getNextPrismFpsStreaks({
        health: getPrismFpsWindowHealth(sample),
        streaks: {
          badStreak: badStreakRef.current,
          severeStreak: severeStreakRef.current,
        },
      });
      badStreakRef.current = nextStreaks.badStreak;
      severeStreakRef.current = nextStreaks.severeStreak;

      if (shouldDowngradeForFpsStreaks(nextStreaks)) {
        downgrade();
      }
    },
    [downgrade, isEntranceComplete],
  );

  // The WebGL-failure entry point IS the downgrade transition — nothing to add on top of it.
  return { mode, recordFpsSample, reportPrismFailure: downgrade };
}
